const safeTrim = (value) => String(value ?? '').trim();

/**
 * Parse an option symbol or description into underlying root ticker and option type ('C' or 'P').
 * Handles OCC symbols (e.g. AAPL240719C00150000, -TSLA240719C305) and human-readable descriptions
 * (e.g. "TSLA 07/19/2024 305.00 C", "AAPL 07/19/2024 CALL 150").
 */
export const parseOptionSecurity = (symbol) => {
  const core = String(symbol ?? '').trim().toUpperCase().replace(/^-/, '').trim();
  if (!core) return { underlying: '', optionType: null };

  const compact = core.replace(/\s+/g, '');
  const occMatch = compact.match(/^([A-Z][A-Z0-9.]{0,5})(\d{6})([CP])(\d{1,8})$/);
  if (occMatch && !/^[A-Z][A-Z0-9.]{0,5}$/.test(core)) {
    return { underlying: occMatch[1], optionType: occMatch[3] };
  }

  const descriptionMatch = core.match(
    /^([A-Z][A-Z0-9.]{0,5})\s*(?:-|\s+)?\d{1,2}\/\d{1,2}\/\d{2,4}\s+(CALL|PUT)\s*\$?\d+(?:\.\d+)?$/,
  );
  if (descriptionMatch) {
    return {
      underlying: descriptionMatch[1],
      optionType: descriptionMatch[2] === 'CALL' ? 'C' : 'P',
    };
  }

  const tokens = core.split(/\s+/);
  const root = tokens[0];
  if (!/^[A-Z][A-Z0-9.]{0,5}$/.test(root)) {
    return { underlying: core, optionType: null };
  }
  if (tokens.length === 1) {
    return { underlying: root, optionType: null };
  }

  const rest = tokens.slice(1).join(' ');
  if (!/\d/.test(rest)) {
    return { underlying: root, optionType: null };
  }

  if (/\b(C|CALL|CALLS)\b/.test(rest)) {
    return { underlying: root, optionType: 'C' };
  }
  if (/\b(P|PUT|PUTS)\b/.test(rest)) {
    return { underlying: root, optionType: 'P' };
  }

  return { underlying: root, optionType: null };
};

export const getTradeContractMultiplier = (symbol) => (
  parseOptionSecurity(symbol).optionType ? 100 : 1
);

/**
 * Detect whether a trade record represents a call option.
 */
export const isCallOption = (trade) => {
  if (!trade) return false;
  if (trade.is_covered_call === true || trade.covered_call === true) return true;
  if (String(trade.option_type || '').toUpperCase() === 'C' || String(trade.option_type || '').toUpperCase() === 'CALL') {
    return true;
  }
  const secType = String(trade.security_type || '').toUpperCase();
  if (secType.includes('COVERED') || (secType.includes('CALL') && secType.includes('OPTION'))) {
    return true;
  }
  const note = String(trade.parse_note || '').toUpperCase();
  if (note.includes('COVERED CALL')) return true;

  const symbol = String(trade.symbol || '');
  if (/\b(COVERED\s*CALL)\b/i.test(symbol)) return true;

  const parsed = parseOptionSecurity(symbol);
  return parsed.optionType === 'C';
};

/**
 * Detect whether a trade record represents a dividend reinvestment.
 */
export const isDividendReinvestment = (trade) => {
  if (!trade) return false;
  if (trade.is_dividend_reinvestment === true || trade.dividend_reinvestment === true) {
    return true;
  }
  const action = String(trade.action || '').toUpperCase().trim();
  if (action === 'DIVIDEND_REINVESTMENT' || action === 'REINVESTMENT' || action === 'REINVEST') {
    return true;
  }
  if (action.includes('DIVIDEND') || action.includes('REINVEST')) {
    return true;
  }
  return false;
};

export const filterAndSortTrades = (tradeRows, filters = {}, sortConfig = {}) => {
  const normalizedFilters = {
    search: safeTrim(filters.search),
    action: filters.action || 'all',
    source: filters.source || 'all',
    brokerage: filters.brokerage || 'all',
    symbol: filters.symbol || 'all',
    coveredCalls: filters.coveredCalls || filters.covered_calls || 'all',
    dividendReinvestments: filters.dividendReinvestments || filters.dividend_reinvestments || filters.dividends || 'all',
  };

  const normalizedSort = {
    key: sortConfig.key || 'trade_datetime',
    direction: sortConfig.direction === 'asc' ? 'asc' : 'desc',
  };
  const [source, timeStatus] = normalizedFilters.source.split(':');

  const filtered = (tradeRows || []).filter((trade) => {
    const searchTerm = normalizedFilters.search.toLowerCase();
    const matchesSearch = !searchTerm || [
      trade.symbol,
      trade.brokerage,
      trade.account_name,
      trade.account_number,
      trade.action,
    ].some((value) => String(value ?? '').toLowerCase().includes(searchTerm));

    const matchesAction = normalizedFilters.action === 'all' || trade.action === normalizedFilters.action;
    const matchesSource = source === 'all'
      || (source === 'manual' && !(trade.hybridized || trade.auto_processed))
      || (source === 'auto' && Boolean(trade.auto_processed) && !trade.hybridized)
      || (source === 'hybrid' && Boolean(trade.hybridized));
    const matchesTimeStatus = !timeStatus
      || (timeStatus === 'date-only' && Boolean(trade.time_estimated))
      || (timeStatus === 'known-time' && !trade.time_estimated);
    const matchesBrokerage = normalizedFilters.brokerage === 'all' || trade.brokerage === normalizedFilters.brokerage;
    const matchesSymbol = normalizedFilters.symbol === 'all' || trade.symbol === normalizedFilters.symbol;

    const coveredCallsFilter = normalizedFilters.coveredCalls;
    const matchesCallOptions = coveredCallsFilter === 'all'
      || ((coveredCallsFilter === 'exclude' || coveredCallsFilter === 'filter_out' || coveredCallsFilter === 'hide') && !isCallOption(trade))
      || (coveredCallsFilter === 'only' && isCallOption(trade));

    const dividendFilter = normalizedFilters.dividendReinvestments;
    const matchesDividendReinvestments = dividendFilter === 'all'
      || ((dividendFilter === 'exclude' || dividendFilter === 'filter_out' || dividendFilter === 'hide') && !isDividendReinvestment(trade))
      || (dividendFilter === 'only' && isDividendReinvestment(trade));

    return (
      matchesSearch &&
      matchesAction &&
      matchesSource &&
      matchesTimeStatus &&
      matchesBrokerage &&
      matchesSymbol &&
      matchesCallOptions &&
      matchesDividendReinvestments
    );
  });

  const getNumericValue = (trade, key) => {
    if (key === 'quantity') return Number(trade.quantity || 0);
    if (key === 'price') return Number(trade.price || 0);
    if (key === 'notional') return Number(trade.notional || 0);
    if (key === 'trade_datetime') {
      const ts = new Date(trade.trade_datetime || 0).getTime();
      return Number.isNaN(ts) ? 0 : ts;
    }
    return 0;
  };

  const directionFactor = normalizedSort.direction === 'asc' ? 1 : -1;

  return [...filtered].sort((a, b) => {
    const key = normalizedSort.key;

    if (key === 'trade_datetime') {
      return (getNumericValue(a, key) - getNumericValue(b, key)) * directionFactor;
    }

    if (['quantity', 'price', 'notional'].includes(key)) {
      return (getNumericValue(a, key) - getNumericValue(b, key)) * directionFactor;
    }

    if (key === 'source') {
      const sourceWeight = (trade) => {
        if (trade.hybridized) return 3;
        if (trade.auto_processed) return 2;
        return 1;
      };
      return (sourceWeight(a) - sourceWeight(b)) * directionFactor;
    }

    if (key === 'action') {
      const order = { BUY: 1, SELL: 2, OTHER: 3 };
      return ((order[String(a.action)] || 99) - (order[String(b.action)] || 99)) * directionFactor;
    }

    const left = String(a[key] ?? '').toLowerCase();
    const right = String(b[key] ?? '').toLowerCase();
    return left.localeCompare(right) * directionFactor;
  });
};
